import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { ImageUploader } from '../../components/ImageUploader';
import { api, type ApiSuccess } from '../../lib/api';

type Section = 'services' | 'faqs' | 'ads';

export function AdminContentPage() {
  const [section, setSection] = useState<Section>('services');
  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">📦 المحتوى</h1>
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={section === 'services'} onClick={() => setSection('services')}>🛠️ الخدمات</Tab>
        <Tab active={section === 'faqs'} onClick={() => setSection('faqs')}>❓ الأسئلة الشائعة</Tab>
        <Tab active={section === 'ads'} onClick={() => setSection('ads')}>📢 الإعلانات (بنرات)</Tab>
      </div>
      {section === 'services' && <ServicesAdmin />}
      {section === 'faqs' && <FaqsAdmin />}
      {section === 'ads' && <AdsAdmin />}
    </div>
  );
}

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-ink-muted hover:text-navy-dark'
    }`}>{children}</button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Services ━━━━━━━━━━━━━━━━━━━━

interface Service { id: number; icon: string | null; title: string; titleEn: string | null; description: string | null; linkUrl: string | null; sortOrder: number; active: boolean }

function ServicesAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Service | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin', 'services'],
    queryFn: () => api.get<ApiSuccess<Service[]>>('/api/admin/services'),
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => api.delete(`/api/admin/services/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });
  const toggleM = useMutation({
    mutationFn: (s: Service) => api.put(`/api/admin/services/${s.id}`, { ...s, active: !s.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'services'] }),
  });

  const list = data?.data ?? [];
  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="primary" onClick={() => setOpenCreate(true)}>+ خدمة جديدة</Button></div>
      {!list.length ? (
        <Card><CardBody><div className="text-center py-10 text-ink-muted">لا توجد خدمات</div></CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((s) => (
            <Card key={s.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-3xl">{getIcon(s.icon)}</span>
                    <div>
                      <div className="font-black">{s.title}</div>
                      <p className="text-xs text-ink-muted line-clamp-2">{s.description}</p>
                      <Badge color={s.active ? 'green' : 'red'}>{s.active ? 'مفعّلة' : 'موقوفة'}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => toggleM.mutate(s)}>{s.active ? '⏸' : '▶'}</Button>
                    <Button size="sm" variant="primary" onClick={() => setEditing(s)}>✏️</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${s.title}؟`)) deleteM.mutate(s.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {(openCreate || editing) && (
        <ServiceModal
          service={editing}
          onClose={() => { setOpenCreate(false); setEditing(null); }}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'services'] })}
        />
      )}
    </>
  );
}

function ServiceModal({ service, onClose, onSaved }: { service: Service | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    icon: service?.icon ?? '',
    title: service?.title ?? '',
    titleEn: service?.titleEn ?? '',
    description: service?.description ?? '',
    linkUrl: service?.linkUrl ?? '',
    sortOrder: String(service?.sortOrder ?? 0),
    active: service?.active ?? true,
  });
  const m = useMutation({
    mutationFn: () => service
      ? api.put(`/api/admin/services/${service.id}`, { ...f, sortOrder: Number(f.sortOrder) })
      : api.post('/api/admin/services', { ...f, sortOrder: Number(f.sortOrder) }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <Modal title={service ? 'تعديل خدمة' : 'خدمة جديدة'} onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="الأيقونة (home/gavel/building)" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} dir="ltr" />
        <Input label="العنوان *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Input label="العنوان بالإنجليزي" value={f.titleEn} onChange={(e) => setF({ ...f, titleEn: e.target.value })} dir="ltr" />
        <Input label="ترتيب العرض" type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
        <div className="md:col-span-2">
          <label className="block text-sm font-bold mb-1">الوصف</label>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
        </div>
        <Input label="رابط (اختياري)" value={f.linkUrl} onChange={(e) => setF({ ...f, linkUrl: e.target.value })} dir="ltr" />
        <label className="flex items-center gap-2 mt-7">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5 accent-navy" />
          <span className="text-sm font-bold">مفعّلة</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.title}>💾 حفظ</Button>
      </div>
    </Modal>
  );
}

function getIcon(n: string | null) { return { home: '🏠', gavel: '🔨', building: '🏢' }[n ?? ''] ?? '✨'; }

// ━━━━━━━━━━━━━━━━━━━━ FAQs ━━━━━━━━━━━━━━━━━━━━

interface Faq { id: number; category: string; question: string; answer: string; sortOrder: number; active: boolean }

function FaqsAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Faq | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin', 'faqs'],
    queryFn: () => api.get<ApiSuccess<Faq[]>>('/api/admin/faqs'),
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => api.delete(`/api/admin/faqs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'faqs'] }),
  });

  const list = data?.data ?? [];
  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="primary" onClick={() => setOpenCreate(true)}>+ سؤال جديد</Button></div>
      {!list.length ? (
        <Card><CardBody><div className="text-center py-10 text-ink-muted">لا توجد أسئلة</div></CardBody></Card>
      ) : (
        <div className="space-y-2">
          {list.map((f) => (
            <Card key={f.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <Badge color="navy">{f.category}</Badge>
                    <h3 className="font-bold mt-2">{f.question}</h3>
                    <p className="text-sm text-ink-secondary line-clamp-2 mt-1">{f.answer}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="primary" onClick={() => setEditing(f)}>✏️</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف؟')) deleteM.mutate(f.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {(openCreate || editing) && (
        <FaqModal faq={editing} onClose={() => { setOpenCreate(false); setEditing(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'faqs'] })} />
      )}
    </>
  );
}

function FaqModal({ faq, onClose, onSaved }: { faq: Faq | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    category: faq?.category ?? 'general',
    question: faq?.question ?? '',
    answer: faq?.answer ?? '',
    sortOrder: String(faq?.sortOrder ?? 0),
    active: faq?.active ?? true,
  });
  const m = useMutation({
    mutationFn: () => faq
      ? api.put(`/api/admin/faqs/${faq.id}`, { ...f, sortOrder: Number(f.sortOrder) })
      : api.post('/api/admin/faqs', { ...f, sortOrder: Number(f.sortOrder) }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <Modal title={faq ? 'تعديل سؤال' : 'سؤال جديد'} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-bold mb-1">الفئة</label>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
            <option value="general">عام</option>
            <option value="properties">العقارات</option>
            <option value="auctions">المزادات</option>
            <option value="payments">المدفوعات</option>
            <option value="brokers">المسوّقون</option>
            <option value="associations">اتحاد الملاك</option>
          </select>
        </div>
        <Input label="السؤال *" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
        <div>
          <label className="block text-sm font-bold mb-1">الجواب *</label>
          <textarea value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} rows={5} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
        </div>
        <Input label="ترتيب" type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.question || !f.answer}>💾 حفظ</Button>
      </div>
    </Modal>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Ads ━━━━━━━━━━━━━━━━━━━━

interface Ad { id: string; title: string; body: string | null; ctaText: string | null; ctaLink: string | null; imageUrl: string | null; placement: string; color: string; priority: number; clicksCount: number; viewsCount: number; active: boolean }

function AdsAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Ad | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin', 'ads'],
    queryFn: () => api.get<ApiSuccess<Ad[]>>('/api/admin/ads'),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/ads/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ads'] }),
  });

  const list = data?.data ?? [];
  return (
    <>
      <div className="flex justify-end mb-3"><Button variant="primary" onClick={() => setOpenCreate(true)}>+ بنر جديد</Button></div>
      {!list.length ? (
        <Card><CardBody><div className="text-center py-10 text-ink-muted">لا توجد بنرات</div></CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((a) => (
            <Card key={a.id} style={{ borderRight: `4px solid ${a.color}` }}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <Badge color="navy">{a.placement}</Badge>
                    <h3 className="font-bold mt-2">{a.title}</h3>
                    <p className="text-xs text-ink-muted line-clamp-2 mt-1">{a.body}</p>
                    <div className="flex gap-3 mt-2 text-xs text-ink-muted">
                      <span>👁 {a.viewsCount}</span>
                      <span>🖱 {a.clicksCount}</span>
                      <Badge color={a.active ? 'green' : 'red'}>{a.active ? 'نشط' : 'موقوف'}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="primary" onClick={() => setEditing(a)}>✏️</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف؟')) deleteM.mutate(a.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {(openCreate || editing) && (
        <AdModal ad={editing} onClose={() => { setOpenCreate(false); setEditing(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'ads'] })} />
      )}
    </>
  );
}

function AdModal({ ad, onClose, onSaved }: { ad: Ad | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    title: ad?.title ?? '', body: ad?.body ?? '',
    ctaText: ad?.ctaText ?? '', ctaLink: ad?.ctaLink ?? '',
    imageUrl: ad?.imageUrl ?? '',
    placement: ad?.placement ?? 'home_middle',
    color: ad?.color ?? '#1e3a5f',
    priority: String(ad?.priority ?? 0),
    active: ad?.active ?? true,
  });
  const m = useMutation({
    mutationFn: () => ad
      ? api.put(`/api/admin/ads/${ad.id}`, { ...f, priority: Number(f.priority) })
      : api.post('/api/admin/ads', { ...f, priority: Number(f.priority) }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <Modal title={ad ? 'تعديل بنر' : 'بنر جديد'} onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Input label="العنوان *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-bold mb-1">المحتوى</label>
          <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
        </div>
        <Input label="نص زر CTA" value={f.ctaText} onChange={(e) => setF({ ...f, ctaText: e.target.value })} />
        <Input label="رابط زر CTA" value={f.ctaLink} onChange={(e) => setF({ ...f, ctaLink: e.target.value })} dir="ltr" />
        <ImageUploader
          value={f.imageUrl}
          onChange={(url) => setF({ ...f, imageUrl: url ?? '' })}
          purpose="AD_IMAGE"
          label="🖼️ صورة الإعلان"
          hint="حد أقصى 2MB"
          shape="square"
          size="md"
        />
        <div>
          <label className="block text-sm font-bold mb-1">الموقع</label>
          <select value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
            <option value="home_top">رئيسية - أعلى</option>
            <option value="home_middle">رئيسية - وسط</option>
            <option value="home_bottom">رئيسية - أسفل</option>
            <option value="properties_top">العقارات - أعلى</option>
            <option value="auctions_top">المزادات - أعلى</option>
            <option value="sidebar">الشريط الجانبي</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-bold mb-1">اللون</label>
          <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="w-full h-12 rounded-xl border-2 border-gray-200" />
        </div>
        <Input label="الأولوية" type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} hint="الأعلى = يظهر أولاً" />
        <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">نشط</span></label>
      </div>
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.title}>💾 حفظ</Button>
      </div>
    </Modal>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Modal Wrapper ━━━━━━━━━━━━━━━━━━━━

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{title}</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
